<template>
  <div class="question-form-node" :class="nodeClass">
    <van-cell class="question" title-class="title" label-class="label">
      <template #title>
        <div class="question-title" :class="{ 'root-question-title': questionNumber !== null }">
          <span v-if="questionNumber !== null" class="question-number">{{ $t(`${i18nPrefix}.label.questionnaireQuestion`) }} {{ questionNumber }}<span v-if="field.required" class="required-mark">*</span>：</span>
          <span class="question-title-text" :class="{ 'is-required': questionNumber === null && field.required }" v-html="field.title"></span>
        </div>
      </template>
      <template #label>
        <uploaderFile
          v-if="field.elType === 'file'"
          ref="upload"
          class="upload-file"
          :field="field"
          :data="data"
        />
        <vanCustomField v-else :data="data" :field="field">
          <template v-if="usesOptionSlot" v-slot:[field.optionSlot]>
            <component
              :is="choiceGroupComponent"
              v-model="data[field.name]"
              icon-size=""
              :direction="field.direction || 'vertical'"
              :disabled="field.disabledAll"
              class="tree-choice-group"
            >
              <div v-for="(option, index) in field.options.data" :key="index" class="tree-option-block">
                <component
                  :is="choiceComponent"
                  :name="optionValue(option)"
                  :shape="field.elType === 'checkbox' ? (option.shape || 'square') : option.shape"
                  :disabled="option.disabled"
                >
                  {{ optionLabel(option) }}
                </component>
                <div v-if="isOptionSelected(option)" class="tree-option-after">
                  <QuestionFormNode
                    v-for="child in branchFieldsForOption(option)"
                    :key="child.id"
                    :field="child"
                    :form-line="formLine"
                    :data="data"
                    :presentation="presentation"
                    :flattened="true"
                    @register-uploader="$emit('register-uploader', $event)"
                    @unregister-uploader="$emit('unregister-uploader', $event)"
                  />
                </div>
              </div>
            </component>
          </template>
        </vanCustomField>
      </template>
    </van-cell>
  </div>
</template>

<script>
import vanCustomField from './vanCustomField.vue'
import uploaderFile from './uploadFile.vue'

export default {
  name: 'QuestionFormNode',
  components: {
    vanCustomField,
    uploaderFile
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    formLine: {
      type: Array,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    presentation: {
      type: String,
      default: ''
    },
    questionNumber: {
      type: Number,
      default: null
    },
    ancestorIds: {
      type: Array,
      default: () => []
    },
    flattened: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    i18nPrefix () {
      return 'admin.application.candidate'
    },
    isCandidateMobile () {
      return this.presentation === 'candidate-mobile'
    },
    nodeClass () {
      return {
        'candidate-mobile-question': this.isCandidateMobile,
        'candidate-mobile-child': this.questionNumber === null
      }
    },
    usesOptionSlot () {
      return this.isCandidateMobile && !this.flattened && Boolean(this.field.optionSlot)
    },
    choiceGroupComponent () {
      return this.field.elType === 'checkbox' ? 'van-checkbox-group' : 'van-radio-group'
    },
    choiceComponent () {
      return this.field.elType === 'checkbox' ? 'van-checkbox' : 'van-radio'
    },
    nextAncestorIds () {
      return [...this.ancestorIds, String(this.field.id)]
    }
  },
  mounted () {
    if (this.$refs.upload) this.$emit('register-uploader', this.$refs.upload)
  },
  beforeDestroy () {
    if (this.$refs.upload) this.$emit('unregister-uploader', this.$refs.upload)
  },
  methods: {
    optionValue (option) {
      return this.field.options.valueKey ? option[this.field.options.valueKey] : option.value
    },
    optionLabel (option) {
      return this.field.options.labelKey ? option[this.field.options.labelKey] : option.label
    },
    isOptionSelected (option) {
      const answer = this.data[this.field.name]
      const value = this.optionValue(option)
      return Array.isArray(answer) ? answer.map(String).includes(String(value)) : String(answer) === String(value)
    },
    childrenForOption (option) {
      return this.childrenForFieldOption(this.field, option, this.nextAncestorIds)
    },
    childrenForFieldOption (parentField, option, ancestorIds) {
      const optionId = String(parentField.options.valueKey ? option[parentField.options.valueKey] : option.value)
      return this.formLine.filter(child => {
        const tree = child.tree || {}
        return tree.parentQuestionId === String(parentField.id) &&
          tree.parentOptionId === optionId &&
          !ancestorIds.includes(String(child.id))
      })
    },
    selectedOptionsForField (field) {
      if (!field.options) return []
      const answer = this.data[field.name]
      return field.options.data.filter(option => {
        const value = field.options.valueKey ? option[field.options.valueKey] : option.value
        return Array.isArray(answer) ? answer.map(String).includes(String(value)) : String(answer) === String(value)
      })
    },
    branchFieldsForOption (option) {
      const collect = (parentField, parentOption, ancestorIds) => {
        const children = this.childrenForFieldOption(parentField, parentOption, ancestorIds)
        return children.reduce((result, child) => {
          const childAncestors = [...ancestorIds, String(child.id)]
          result.push(child)
          this.selectedOptionsForField(child).forEach(selectedOption => {
            result.push(...collect(child, selectedOption, childAncestors))
          })
          return result
        }, [])
      }
      return collect(this.field, option, this.nextAncestorIds)
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-mobile-question {
  width: 100%;

  &:not(.candidate-mobile-child) + .candidate-mobile-question:not(.candidate-mobile-child) {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #DCDFE6;
  }

  &.candidate-mobile-child {
    box-sizing: border-box;
    margin-top: 10px;
    padding: 10px;
    border: 1px solid #DCDFE6;
    border-radius: 2px;
  }

  .question.van-cell {
    padding: 0;
  }

  .question-title {
    color: #333;
    font-size: 14px;
    font-weight: 500;
    line-height: normal;
    overflow-wrap: anywhere;
  }

  .question-number {
    display: block;
    font-weight: 400;
    line-height: normal;
  }

  .root-question-title .question-title-text {
    display: block;
    margin-top: 8px;
  }

  .required-mark,
  .question-title-text.is-required::after {
    display: inline-block;
    color: #F56C6C;
    content: '*';
    line-height: 1;
    vertical-align: middle;
    transform: translateY(1px);
  }

  .question-title-text.is-required::after {
    margin-left: 2px;
  }

  .label {
    margin-top: 8px;
  }

  .tree-choice-group {
    width: 100%;
  }

  .tree-option-block + .tree-option-block {
    margin-top: 10px;
  }

  ::v-deep {
    .field-container.van-cell {
      width: 100%;
    }

    .question-text {
      .van-field__body {
        padding: 8px;
        border: 1px solid #DCDFE6;
        border-radius: 4px;
      }

      .van-field__control {
        color: #606266;
        font-size: 14px;
        line-height: 20px;
      }
    }
  }
}
</style>
